import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { PrintInQueue } from '~reducers';
import * as actions from '~actions';
import { isEpsonPrinter, useEpsonDirectPrint } from '~Components/PrinterManager/epsonDirectPrint';
import { createEpsonImage } from './utils';

type PrintData = Array<{
  fontSize?: number;
  alignment?: string;
  qr?: boolean;
  content: string;
  image?: {
    width: number;
    height: number;
    data: string;
  };
}>;

/**
 * Waits for the native bridge to respond with success or failure for a specific print.
 * Listens for window messages from the native app (same messages App.tsx handles).
 */
const waitForBridgeResponse = (
  printId: string,
  timeoutMs = 15000,
): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve({ success: false, error: 'Timeout waiting for bridge response' });
    }, timeoutMs);

    const handler = (event: MessageEvent) => {
      let data = event.data;
      try {
        data = JSON.parse(data);
      } catch {
        window.removeEventListener('message', handler);
        resolve({ success: false, error: 'Invalid JSON response from bridge' });
      }

      if (data.id === printId && data.type === 'PRINT_BRIDGE_REQUEST_SUCCESS') {
        clearTimeout(timeout);
        window.removeEventListener('message', handler);
        resolve({ success: true });
      } else if (data.id === printId && data.type === 'PRINT_BRIDGE_REQUEST_FAILED') {
        clearTimeout(timeout);
        window.removeEventListener('message', handler);
        resolve({ success: false, error: data.error });
      }
    };

    window.addEventListener('message', handler);
  });
};

/**
 * Hook that provides Epson retry print functionality.
 *
 * For app versions > 6.0.4: sends directly to the printer via HTTP ePOS endpoint
 * using bridgeApi.fetch (the "new" method).
 *
 * For app versions <= 6.0.4: sends via the native bridge (postMessageBridge)
 * with automatic retry, since bridgeApi.fetch is not available.
 */
const useEpsonRetryPrint = () => {
  const logger = useLogger('printer');
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const [storageData] = useStorage();
  const { printViaEpsonWithRetry } = useEpsonDirectPrint();
  const appVersion = useSelector((state) => state.appVersion);

  /**
   * Builds the bridge print data object for an Epson printer, including image processing.
   * Same format as the default postMessageBridge path.
   */
  const buildEpsonBridgePrintData = async (nxtPrint: PrintInQueue) => {
    let imageData: { data: string; width: number; height: number } | null = null;

    if (nxtPrint.printerModel.startsWith('TM-') && nxtPrint.base64Image) {
      try {
        const paperWidth = 570;
        imageData = await createEpsonImage(nxtPrint.base64Image, logger, paperWidth);
      } catch (error) {
        logger.error('Failed to process image for Epson bridge print', {
          errorMessage: error instanceof Error ? error.message : String(error),
          printerId: nxtPrint.printer,
        });
      }
    }

    const formattedData =
      typeof nxtPrint.data === 'string'
        ? nxtPrint.data
        : nxtPrint.data?.map((row) => ({
            ...row,
            alignment: row.alignment?.toLowerCase(),
          })) || '';

    const printData: {
      id: string;
      queueId: string;
      data: PrintData | string;
    } = {
      queueId: nxtPrint.id,
      data:
        nxtPrint.base64Image && imageData && typeof formattedData !== 'string'
          ? [{ content: '', image: imageData }, ...formattedData]
          : nxtPrint.base64Image && imageData && typeof formattedData === 'string'
          ? [{ content: '', image: imageData }, { content: formattedData }]
          : formattedData,
      id: nxtPrint.printer,
    };

    return printData;
  };

  /**
   * Send a print job to an Epson printer via the native bridge (postMessageBridge)
   * with automatic retry. Used for app versions <= 6.0.4 where bridgeApi.fetch
   * is not available for direct HTTP ePOS printing.
   */
  const sendEpsonBridgePrintWithRetry = async (nxtPrint: PrintInQueue) => {
    const maxRetries = 3;
    const retryDelay = 5000;

    const printData = await buildEpsonBridgePrintData(nxtPrint);

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      logger.info('Epson bridge print attempt', {
        queueId: nxtPrint.id,
        printerId: nxtPrint.printer,
        attempt,
        maxRetries,
      });

      // Send via native bridge
      window.postMessageBridge({ print: printData });

      // Wait for the native app to respond
      const result = await waitForBridgeResponse(nxtPrint.id);

      if (result.success) {
        // App.tsx already dispatched SUCCESS to Redux
        logger.info('Epson bridge print succeeded', {
          queueId: nxtPrint.id,
          attempt,
        });
        return;
      }

      // App.tsx already dispatched FAILED to Redux — reset state for retry
      if (attempt < maxRetries - 1) {
        logger.info('Epson bridge print failed, retrying', {
          queueId: nxtPrint.id,
          attempt,
          bridgeError: result.error,
          nextRetryIn: `${retryDelay / 1000}s`,
        });

        // Reset print back to isPrinting so PrintInstance doesn't show error during retry
        dispatch(actions.printQueue(nxtPrint.id));
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    // All retries exhausted — the last failure was already dispatched by App.tsx
    logger.error('Epson bridge print failed after all retries', {
      queueId: nxtPrint.id,
      printerId: nxtPrint.printer,
      printType: nxtPrint.printType,
      totalAttempts: maxRetries,
    });
  };

  /**
   * Send a print job to an Epson printer with retry.
   *
   * Routes to the appropriate method based on app version:
   * - App > 6.0.4: direct HTTP ePOS via bridgeApi.fetch
   * - App <= 6.0.4: native bridge (postMessageBridge) with automatic retry
   */
  const sendEpsonDirectPrint = async (nxtPrint: PrintInQueue) => {
    // App versions <= 6.0.4 don't have bridgeApi.fetch, use native bridge with retry instead
    if (appVersion !== 'browser' && appVersion <= '6.0.4') {
      await sendEpsonBridgePrintWithRetry(nxtPrint);
      return;
    }

    logger.info('Routing print to Epson direct (ePOS HTTP)', {
      queueId: nxtPrint.id,
      printerId: nxtPrint.printer,
      printType: nxtPrint.printType,
    });

    const success = await printViaEpsonWithRetry(
      nxtPrint.printer,
      nxtPrint.data || '',
      logger,
      nxtPrint.base64Image,
    );

    if (success) {
      logger.info('Epson direct print sent successfully', {
        queueId: nxtPrint.id,
      });

      dispatch({
        type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_SUCCESS,
        id: nxtPrint.id,
      });

      if (nxtPrint.printType === 'BONG') {
        dispatch(actions.addToast(i18n('BONG.sent'), true, 'epson-direct-' + nxtPrint.printer));
      }
    } else {
      logger.error('Epson direct print failed after all retries', {
        queueId: nxtPrint.id,
        printerId: nxtPrint.printer,
        printType: nxtPrint.printType,
      });

      dispatch({
        type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED,
        id: nxtPrint.id,
        error: 'Epson direct print failed after retries',
      });
    }
  };

  /**
   * Check if a print should be handled by the Epson retry path.
   */
  const shouldUseEpsonRetry = (printerModel: string) => {
    return storageData.enableEpsonRetry && isEpsonPrinter(printerModel);
  };

  return { sendEpsonDirectPrint, shouldUseEpsonRetry };
};

export default useEpsonRetryPrint;
